import { useCallback, useEffect, useRef, useState } from "react";
import { uploadAttachment, OFFLINE } from "../core/api";
import { useToast } from "../../contexts/ToastContext";

export const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
];

export const AttachmentStatus = {
  UPLOADING: "uploading",
  READY: "ready",
  ERROR: "error",
};

export const MAX_ATTACHMENTS = 5;

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the image file."));
    reader.readAsDataURL(file);
  });

const imageFilesFrom = (source) => {
  const files = Array.from(source.files || []).filter((file) =>
    file.type.startsWith("image/"),
  );
  if (files.length) return files;

  return Array.from(source.items || [])
    .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
    .map((item) => item.getAsFile())
    .filter(Boolean);
};

const isFileDrag = (e) => Array.from(e.dataTransfer.types).includes("Files");

const rejectReason = (file) => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return `${file.name}: only PNG, JPEG, WebP and GIF images can be attached`;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `${file.name}: too large, the maximum size is 5MB`;
  }
  return null;
};

export function useAttachments({ domain, requirePaidPlan }) {
  const { showToast } = useToast();
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const nextIdRef = useRef(0);
  const previewUrlsRef = useRef(new Map());
  // The picked File is kept so a failed upload can be retried without making the
  // user find the file again.
  const filesRef = useRef(new Map());
  // dragenter/dragleave fire again for every child the cursor crosses, so the
  // overlay has to count them instead of trusting a single leave.
  const dragDepthRef = useRef(0);
  // The upload callbacks outlive the render they were created in, so capacity
  // and retry targets have to be read from a ref rather than a captured array.
  const attachmentsRef = useRef([]);

  const update = useCallback((updater) => {
    setAttachments((current) => {
      const next = updater(current);
      attachmentsRef.current = next;
      return next;
    });
  }, []);

  const settle = useCallback(
    (id, fields) =>
      update((current) =>
        current.map((item) => (item.id === id ? { ...item, ...fields } : item)),
      ),
    [update],
  );

  const forget = useCallback((id) => {
    const url = previewUrlsRef.current.get(id);
    if (url) URL.revokeObjectURL(url);
    previewUrlsRef.current.delete(id);
    filesRef.current.delete(id);
  }, []);

  const removeAttachment = useCallback(
    (id) => {
      forget(id);
      update((current) => current.filter((item) => item.id !== id));
    },
    [forget, update],
  );

  const clearAttachments = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
    filesRef.current.clear();
    update(() => []);
  }, [update]);

  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const startUpload = useCallback(
    async (id) => {
      const file = filesRef.current.get(id);
      if (!file) return;

      settle(id, { status: AttachmentStatus.UPLOADING, error: null, offline: false });
      try {
        const dataUrl = await readAsDataUrl(file);
        const { url } = await uploadAttachment(domain, dataUrl);
        settle(id, { status: AttachmentStatus.READY, url, error: null, offline: false });
      } catch (err) {
        settle(id, {
          status: AttachmentStatus.ERROR,
          error: err.message,
          offline: err.code === OFFLINE,
        });
      }
    },
    [domain, settle],
  );

  // Anything that died with the connection comes back on its own, so a dropped
  // signal costs the user nothing.
  useEffect(() => {
    const resume = () =>
      attachmentsRef.current
        .filter((item) => item.status === AttachmentStatus.ERROR && item.offline)
        .forEach((item) => startUpload(item.id));

    window.addEventListener("online", resume);
    return () => window.removeEventListener("online", resume);
  }, [startUpload]);

  const attachFiles = useCallback(
    async (incoming) => {
      const files = Array.from(incoming || []);
      if (!files.length) return;

      // One unusable file must not throw away the good ones dropped with it.
      const accepted = files.filter((file) => {
        const reason = rejectReason(file);
        if (reason) showToast(reason, "warning");
        return !reason;
      });
      if (!accepted.length) return;

      const room = MAX_ATTACHMENTS - attachmentsRef.current.length;
      if (accepted.length > room) {
        showToast(`You can attach up to ${MAX_ATTACHMENTS} images`, "warning");
      }
      if (room <= 0) return;

      const queued = accepted.slice(0, room).map((file) => {
        const id = nextIdRef.current++;
        const previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.set(id, previewUrl);
        filesRef.current.set(id, file);
        return {
          id,
          status: AttachmentStatus.UPLOADING,
          previewUrl,
          name: file.name,
          url: null,
          error: null,
          offline: false,
        };
      });

      // The chips go up before any network call. The plan check below is a
      // request of its own, and on a slow link the user would otherwise stare at
      // an empty composer and send the message without the images.
      update((current) => [...current, ...queued]);

      if (!(await requirePaidPlan())) {
        queued.forEach((item) => removeAttachment(item.id));
        return;
      }

      // Each upload settles its own chip, so one failure leaves the rest alone.
      await Promise.all(queued.map((item) => startUpload(item.id)));
    },
    [requirePaidPlan, showToast, update, removeAttachment, startUpload],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      // Picking the same file twice fires no change event unless we reset this.
      e.target.value = "";
      attachFiles(files);
    },
    [attachFiles],
  );

  const onPaste = useCallback(
    (e) => {
      const files = imageFilesFrom(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      attachFiles(files);
    },
    [attachFiles],
  );

  const dragProps = {
    onDragEnter: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    },
    onDragOver: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e) => {
      if (!isFileDrag(e)) return;
      dragDepthRef.current -= 1;
      if (dragDepthRef.current <= 0) {
        dragDepthRef.current = 0;
        setIsDragging(false);
      }
    },
    onDrop: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      attachFiles(e.dataTransfer.files);
    },
  };

  return {
    attachments,
    readyAttachments: attachments.filter(
      (item) => item.status === AttachmentStatus.READY,
    ),
    pending: attachments.some(
      (item) => item.status !== AttachmentStatus.READY,
    ),
    isFull: attachments.length >= MAX_ATTACHMENTS,
    isDragging,
    fileInputRef,
    dragProps,
    openFilePicker,
    onFileInputChange,
    onPaste,
    retryAttachment: startUpload,
    removeAttachment,
    clearAttachments,
  };
}
